import React from "react";

import { Alert, Box, Button, Paper, Typography } from "@mui/material";

import VideocamIcon from "@mui/icons-material/Videocam";
import MicIcon from "@mui/icons-material/Mic";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import WifiIcon from "@mui/icons-material/Wifi";

import { MEDIA_MODE } from "hook/useDevices";

import MicLevelBars from "./MicLevelBars";
import SpeakerCheck from "./SpeakerCheck";

const STATUS_CONFIG = {
  checking: {
    iconColor: "grey.400",
    bg: "grey.50",
    border: "grey.200",
    dot: "#9e9e9e",
    label: "Checking...",
    color: "text.disabled",
  },

  pass: {
    iconColor: "success.main",
    bg: "rgba(46,125,50,0.04)",
    border: "rgba(46,125,50,0.18)",
    dot: "#2e7d32",
    label: "Ready",
    color: "success.main",
  },

  fail: {
    iconColor: "error.main",
    bg: "rgba(211,47,47,0.04)",
    border: "rgba(211,47,47,0.18)",
    dot: "#d32f2f",
    label: "Needs Fix",
    color: "error.main",
  },

  warn: {
    iconColor: "warning.main",
    bg: "rgba(237,108,2,0.04)",
    border: "rgba(237,108,2,0.18)",
    dot: "#ed6c02",
    label: "Fair",
    color: "warning.main",
  },

  idle: {
    iconColor: "grey.400",
    bg: "grey.50",
    border: "grey.200",
    dot: "#bdbdbd",
    label: "Not Required",
    color: "text.disabled",
  },
};

const CheckRow = ({ icon, label, status, detail, action }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.idle;

  return (
    <Box
      sx={{
        px: {
          xs: 1.5,
          sm: 2,
        },
        py: 1.5,
        mb: 1,
        borderRadius: 1.5,
        bgcolor: config.bg,
        border: "1px solid",
        borderColor: config.border,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            color: config.iconColor,
            display: "flex",
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            {label}
          </Typography>

          {detail && (
            <Box
              sx={{
                mt: 0.4,
              }}
            >
              {typeof detail === "string" ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.5,
                  }}
                >
                  {detail}
                </Typography>
              ) : (
                detail
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: config.dot,
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: config.color,
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {config.label}
            </Typography>
          </Box>

          {action}
        </Box>
      </Box>
    </Box>
  );
};

const DeviceChecks = ({
  devices,
  mediaMode,

  hasIssue,
  allGreen,

  onFix,

  showNetwork = false,
  netStep = "checking",
  netQuality = null,
}) => {
  const {
    camStatus,
    micStatus,

    speakerStatus,
    setSpeakerStatus,

    speakerCheckActive,
    setSpeakerCheckActive,

    micLevel,
    micOn,
  } = devices;

  const isAudioOnly = mediaMode === MEDIA_MODE.AUDIO_ONLY;

  const fixButton = (
    <Button
      size="small"
      color="error"
      onClick={onFix}
      sx={{
        fontSize: 11,
        px: 1,
        minWidth: 0,
      }}
    >
      Fix
    </Button>
  );

  const speakerDetail =
    speakerStatus === "pass"
      ? "You confirmed that the test sound is working."
      : speakerStatus === "fail"
        ? "You could not hear the test sound."
        : speakerCheckActive
          ? "Listen for the test sound."
          : "Speaker check will start after your microphone is ready.";

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Is everything ready?
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {hasIssue
            ? "Some items need your attention before joining."
            : allGreen
              ? "Your selected consultation setup is ready."
              : "Checking your devices..."}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 1.5,
          pb: 1.5,
        }}
      >
        <CheckRow
          icon={<VideocamIcon fontSize="small" />}
          label="Camera"
          status={isAudioOnly ? "idle" : camStatus}
          detail={
            isAudioOnly
              ? "Camera is not required in Privacy Mode."
              : camStatus === "pass"
                ? "Your camera is working."
                : camStatus === "fail"
                  ? "Your camera is unavailable."
                  : "Checking your camera..."
          }
          action={!isAudioOnly && camStatus === "fail" ? fixButton : undefined}
        />

        <CheckRow
          icon={<MicIcon fontSize="small" />}
          label="Microphone"
          status={micStatus}
          detail={
            micStatus === "pass" ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="caption" fontWeight={600}>
                    Say a few words
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    The bars should move when your microphone hears your voice.
                  </Typography>
                </Box>

                <MicLevelBars level={micOn ? micLevel : 0} active={micOn} />
              </Box>
            ) : micStatus === "fail" ? (
              "Your microphone is unavailable."
            ) : (
              "Checking your microphone..."
            )
          }
          action={micStatus === "fail" ? fixButton : undefined}
        />

        <CheckRow
          icon={<VolumeUpIcon fontSize="small" />}
          label="Speaker"
          status={
            speakerStatus === "idle"
              ? speakerCheckActive
                ? "checking"
                : "idle"
              : speakerStatus
          }
          detail={speakerDetail}
        />

        <Box
          sx={{
            px: 1,
            pb: speakerCheckActive && speakerStatus !== "pass" ? 1.5 : 0,
          }}
        >
          <SpeakerCheck
            active={speakerCheckActive && speakerStatus !== "pass"}
            onResult={(heard) => {
              setSpeakerStatus(heard ? "pass" : "fail");

              setSpeakerCheckActive(false);
            }}
          />
        </Box>

        {speakerStatus === "fail" && (
          <Alert
            severity="warning"
            sx={{
              mx: 1,
              mb: 1,
            }}
          >
            No sound? Check your device volume or headphones.
            <Button
              size="small"
              sx={{
                ml: 1,
              }}
              onClick={() => {
                setSpeakerStatus("idle");

                setSpeakerCheckActive(true);
              }}
            >
              Try Again
            </Button>
          </Alert>
        )}

        {showNetwork && (
          <CheckRow
            icon={<WifiIcon fontSize="small" />}
            label="Connection"
            status={netStep}
            detail={
              netQuality === null
                ? "Checking your internet connection..."
                : netQuality >= 3
                  ? "Strong connection."
                  : netQuality === 2
                    ? "Good connection."
                    : netQuality === 1
                      ? "Weak connection."
                      : "Connection unavailable."
            }
          />
        )}
      </Box>
    </Paper>
  );
};

export default DeviceChecks;
